import {
    ChangeDetectionStrategy,
    Component,
    inject,
    HostBinding,
    ViewEncapsulation,
    OnInit,
    ViewChild,
    computed,
} from '@angular/core';
import { IOnboardingStepNotification } from '@fi-app/fi-onboarding/fi-onboarding-builder/fi-onboarding';
import { cloneDeep } from '@fi-app/shared/utils/clone-deep';
import { FiDialogsContentBase } from '@fi-shared/fi-dialogs/fi-dialogs-content-base';
import { FiOnboardingStepNotificationRecipientType } from '@fi-app/fi-onboarding/fi-onboarding-enums';
import { FiOnboardingAutomatedNotificationHelperService } from '../fi-onboarding-automated-notification-helper.service';
import { AutomatedActionRecipientActionType } from '@fi-app/shared/automated-action-recipient-select/automated-action-recipient-select-enum';
import { Animations } from '@fi-app/shared/utils/animations';
import { FiValidatingFormDirective } from '@fi-app/shared/fi-directives/fi-validating-form/fi-validating-form.directive';
import { FiValuesComparison } from '@fi-app/shared/utils/fi-values-comparison';
import { FiDialogsService } from '@fi-app/shared/fi-dialogs/fi-dialogs.service';
import { FiOnboardingBuilderStoreService } from '../../../fi-onboarding-builder-store.service';
import { IAccessRightFilterCollection } from '@fi-shared/models';
import { INameIdObj } from '@fi-app/shared/fi-shared-interfaces';

interface IFiOnboardingNotificationEditorDialogData {
    notification: IOnboardingStepNotification;
    reminderTimeOptions: INameIdObj[];
}

@Component({
    selector: 'fi-onboarding-notification-editor-dialog',
    templateUrl: './notification-editor-dialog.component.html',
    styleUrls: ['./notification-editor-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-onboarding-notification-editor-dialog' },
    standalone: false,
    animations: [Animations.fadeHeight()],
})
export class FiOnboardingNotificationEditorDialogComponent
    extends FiDialogsContentBase<IFiOnboardingNotificationEditorDialogData, IOnboardingStepNotification>
    implements OnInit
{
    @ViewChild('sendNotificationEditorForm') sendNotificationEditorForm: FiValidatingFormDirective;
    protected notification: IOnboardingStepNotification = cloneDeep(this.data.notification);

    @HostBinding('class.participants-notification')
    protected get isParticipants(): boolean {
        return this.notification.RecipientType === FiOnboardingStepNotificationRecipientType.Participants;
    }

    protected notifHelperService = inject(FiOnboardingAutomatedNotificationHelperService);
    private fiOnboardingBuilderStoreService = inject(FiOnboardingBuilderStoreService);

    protected readonly accessRightFilterCollection = computed<IAccessRightFilterCollection>(() => {
        return this.fiOnboardingBuilderStoreService.onboardingAccessRightFilterCollection();
    });
    protected readonly ReminderTimeOptions = this.data.reminderTimeOptions;
    protected readonly RecipientTypeOptions = this.notifHelperService.RecipientTypeOptions;
    protected readonly AutomatedActionRecipientActionType = AutomatedActionRecipientActionType;

    private fiDialogsService = inject(FiDialogsService);
    private fiValuesComparison: FiValuesComparison<IOnboardingStepNotification>;

    public ngOnInit(): void {
        this.fiValuesComparison = new FiValuesComparison(() => this.notification);
    }

    protected updateValidity(): void {
        this.sendNotificationEditorForm.updateValidity();
    }

    public getResult(): IOnboardingStepNotification {
        if (this.notifHelperService.isNotificationMethodHidden(this.notification.RecipientType)) {
            this.notification.ViaNotification = false;
            this.notification.ViaSMS = false;
            this.notification.ViaEmail = false;
        }
        return this.notification;
    }

    public getNoButtonClickPromise(): Promise<{ canClose: boolean }> {
        return this.fiDialogsService.getCanCloseConfirmation(!this.fiValuesComparison.isChanged());
    }

    public isYesButtonDisabled(): boolean {
        return !!this.sendNotificationEditorForm.errors || this.sendNotificationEditorForm.pending;
    }
}
